import { Dispatch, FC, SetStateAction, useEffect, useState } from 'react';
import { IndividualStudentReportRoadmapOverallQuery } from '../../graphQLOperations/__generated__/IndividualStudentReportRoadmapOverallQuery.graphql';
import { PreloadedQuery, usePreloadedQuery } from 'react-relay';
import { getStudentRoadmapOverall } from '../../graphQLOperations/IndividualStudentReport';
import { MeterOptionIndividualReport } from '../../models/MeterOption';
import { MeterGroup } from 'primereact/metergroup';
import { IndividualStudentReportMeterItem } from './IndividualStudentReportMeterItem';
import { IndividualStudentReportSkillsModal } from './IndividualStudentReportSkillsModal';
import { SelectedSkillIndividualReport } from './IndividualReportWrapper';

const skillStatus = ['demonstrated', 'almost', 'review', 'keep', 'not'];
const classNames: { [key: string]: string } = {
  demonstrated: 'c-passed',
  almost: 'c-almost-there',
  review: 'c-almost-there',
  keep: 'keep-learning',
  not: 'not-started',
};

interface Props {
  queryDataRef: PreloadedQuery<IndividualStudentReportRoadmapOverallQuery, Record<string, unknown>>;
  currentRoadmap?: { id: string; name?: string | null } | null;
  setSelectedSkill: Dispatch<SetStateAction<SelectedSkillIndividualReport | null>>;
}

export const IndividualStudentOverallMetergroup: FC<Props> = ({ queryDataRef, currentRoadmap, setSelectedSkill }) => {
  const [modalState, setModalState] = useState<string | null>(null);
  const [meterValues, setMeterValues] = useState<MeterOptionIndividualReport[]>([]);
  const queryData = usePreloadedQuery(getStudentRoadmapOverall, queryDataRef);
  const totalSkills = queryData.skillStatesByStudentId.filter(
    sk => !sk.skill?.targetSkills?.every(ts => ts?.targetSkillType?.name === 'Extension'),
  );
  const skillsCompleted = totalSkills.filter(
    sk =>
      sk.skillSubstate?.skillState?.id === 'SkillStateNode:3' ||
      sk.skillSubstate?.skillState?.id === 'SkillStateNode:4' ||
      (sk.skillAssessment?.score && sk.skillAssessment?.score >= 8),
  ).length;

  useEffect(() => {
    const _data: {
      [key: string]: {
        id: string;
        name: string;
        code: string;
        isTarget: boolean;
        skillId: string;
        score?: number;
      }[];
    } = {};
    totalSkills.forEach(sk => {
      let label = '';
      if (
        sk.skillSubstate?.skillState?.id === 'SkillStateNode:3' ||
        sk.skillSubstate?.skillState?.id === 'SkillStateNode:4'
      ) {
        label = 'demonstrated';
      } else if (sk.skillSubstate?.skillState?.id === 'SkillStateNode:6') {
        label = 'review';
      } else if (sk.skillAssessment?.score !== undefined) {
        if (sk.skillAssessment.score >= 8) {
          label = 'demonstrated';
        } else if (sk.skillAssessment.score === 6) {
          label = 'almost';
        } else {
          label = 'keep';
        }
      } else {
        label = 'not';
      }

      if (_data[label]) {
        _data[label] = [
          ..._data[label],
          {
            id: sk.id,
            isTarget: sk.skill?.targetSkills?.some(ts => ts?.targetSkillType?.name === 'Target') || false,
            score: sk.skillSubstate?.skillState?.id === 'SkillStateNode:3' ? undefined : sk.skillAssessment?.score,
            name: sk.skill?.name || '',
            code: sk.skill?.code || '',
            skillId: sk.skill?.id || '',
          },
        ];
      } else {
        _data[label] = [
          {
            id: sk.id,
            isTarget: sk.skill?.targetSkills?.some(ts => ts?.targetSkillType?.name === 'Target') || false,
            score: sk.skillAssessment?.score,
            name: sk.skill?.name || '',
            code: sk.skill?.code || '',
            skillId: sk.skill?.id || '',
          },
        ];
      }
    });
    const values: MeterOptionIndividualReport[] = Object.keys(_data).map(item => {
      const width = Math.floor((_data[item].length * 100) / Math.floor(totalSkills.length));
      const label = item;
      const element = {
        label,
        width,
        skills: _data[item],
        className: classNames[item],
        students: 0,
        values: [],
        percentages: [0],
        totaLPercent: 0,
        margin: false,
      };
      return element;
    });
    values.sort((a, b) => skillStatus.indexOf(a.label) - skillStatus.indexOf(b.label));

    setMeterValues(
      values.map((element, inx) => {
        return { ...element, margin: inx !== 0 };
      }),
    );
  }, [queryData]);

  return (
    <div className="tw-w-full">
      <div className="tw-w-full tw-flex tw-justify-between">
        <h4 className="tw-inline">{currentRoadmap?.name}</h4>
        <div>
          <span className="tw-font-semibold">{Math.round((skillsCompleted * 100) / totalSkills.length)}%</span>
          <span>{` (${skillsCompleted}/${totalSkills.length} skills completed)`}</span>
        </div>
      </div>

      <div className="tw-mt-2">
        <MeterGroup
          className="nc_metergroup w-full nc_metergroup-wide mb-3 overflow-hidden"
          values={meterValues}
          meter={met => (
            <IndividualStudentReportMeterItem
              setIndividualModalState={setModalState}
              key={(met as MeterOptionIndividualReport).label}
              value={met as MeterOptionIndividualReport}
            />
          )}
        />
      </div>
      <IndividualStudentReportSkillsModal
        setSelectedSkill={setSelectedSkill}
        meterValues={meterValues}
        individualModalState={modalState}
        setIndividualModalState={setModalState}
      />
    </div>
  );
};
